import React from "react";
import { motion } from "framer-motion";
import { Sprout, FlaskConical, Package, Ship, FileCheck2 } from "lucide-react";

const STEPS = [
  {
    icon: Sprout,
    title: "Sourcing at Origin",
    body: "Direct procurement from contracted farmers and trusted mandis across India.",
  },
  {
    icon: FlaskConical,
    title: "Quality Testing",
    body: "Each lot is tested for moisture, purity, pesticide residue and microbial load.",
  },
  {
    icon: Package,
    title: "Custom Packaging",
    body: "From 25kg bulk sacks to private-label retail packs — flexible packaging options.",
  },
  {
    icon: FileCheck2,
    title: "Export Documentation",
    body: "Phytosanitary, COO, FSSAI, GST and complete shipping documentation handled.",
  },
  {
    icon: Ship,
    title: "Worldwide Shipping",
    body: "FCL & LCL shipments via JNPT & Mundra ports with real-time tracking.",
  },
];

export const Process = () => {
  return (
    <section
      id="process"
      className="relative py-24 md:py-32 bg-[#0A192F] text-white overflow-hidden grain-overlay"
      data-testid="process-section"
    >
      {/* Ambient gold blur */}
      <div
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 65%)",
        }}
      />
      <div
        className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 65%)",
        }}
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-px w-16 bg-[#D4AF37]" />
          <span className="font-accent text-[10px] text-[#D4AF37]">
            The Process
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="lg:col-span-8 font-display font-bold leading-[0.95] tracking-tight"
            data-testid="process-heading"
            style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
          >
            From Indian farms to your warehouse.
            <br />
            <span className="italic font-normal text-[#D4AF37]">
              In five precise steps.
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-4 text-white/60 text-base leading-relaxed"
          >
            An end-to-end export process designed for consistent
            specifications, full traceability and zero surprises at port.
          </motion.p>
        </div>

        <div className="relative">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
            className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent origin-left"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-6 relative">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.9,
                    delay: 0.2 + i * 0.15,
                    ease: [0.19, 1, 0.22, 1],
                  }}
                  className="relative group"
                  data-testid={`process-step-${i + 1}`}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative w-16 h-16 border border-[#D4AF37]/40 bg-[#060F1E] flex items-center justify-center z-10 group-hover:border-[#D4AF37] transition-colors">
                      <Icon
                        size={24}
                        strokeWidth={1.4}
                        className="text-[#D4AF37]"
                      />
                    </div>
                    <span className="font-display text-4xl font-bold text-white/25">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed max-w-[240px]">
                    {s.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
